import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react"
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import {
  hardwareAddressProblem,
  isValidHardwareAddress,
  LICENCE_LABEL_MAX,
  LICENCE_PORT_KEYS,
  normalizeHardwareAddress,
  portGateErrors,
  portGateWarnings,
  suggestLicenceLabel,
  type Licence,
  type LicenceCatalog,
  type LicenceDownloadRow,
  type LicenceKind,
  type LicencePortKey,
  type LicencePorts,
} from "@xop/dashboard-shared"
import { alpha } from "@mui/material/styles"
import { KeyRound, ShieldCheck, Zap } from "lucide-react"

import { BackButton } from "@/components/ui/back-button"
import { PageHeader } from "@/components/ui/page-header"
import { PageLoader } from "@/components/ui/page-loader"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { saveBlob } from "@/lib/download"
import { PagePermissionGuard } from "@crm-src/components/page-permission-guard"
import usePermissions from "@crm-src/hooks/usePermissions"

import { defaultTrialEnd } from "./create-licence-dialog"
import { DownloadJournalTable } from "./download-journal-table"
import { LicenceFeaturePicker } from "./licence-feature-picker"
import {
  approveLicence,
  createLicence,
  deleteLicence,
  downloadLicenceFile,
  fetchLicence,
  fetchLicenceCatalog,
  fetchLicenceDownloads,
  fetchTrialAvailability,
  issueLicence,
  rejectLicence,
  renewLicence,
  updateLicence,
  type IssueResult,
  type LicencePayload,
} from "./licences-api"
import { PortCountField } from "./port-count-field"

/**
 * The licence builder.
 *
 * ONE PRESS. You pick the terms and press Generate; the licence is created and
 * its file signed in the same step. There is no Save, no Draft, no half-made
 * licence: if the server refuses to sign a brand-new one, the row is removed
 * again and the operator is back where they started.
 *
 * ISSUED MEANS LOCKED. A licence with a file is what a customer holds; editing
 * it would make the row disagree with the file (crm-api refuses too —
 * `LICENCE_LOCKED`). Another machine or different terms is a NEW licence:
 * **Duplicate** prefills this builder from an issued one with the hardware
 * address cleared.
 *
 * THE MOST VALUABLE THING ON THIS SCREEN IS A REFUSAL. The appliance
 * force-disables `conferencing` and `mass_notification` when
 * `ports.audio_conferencing` is zero, and reports that nowhere — the licence
 * installs cleanly and the feature is simply off. `portGateErrors` (shared, so
 * the server and both portals agree) turns that into a blocked Generate button
 * and a sentence saying what would happen. `web_conferencing` is NOT gated by
 * the appliance, so zero web ports is a WARNING, not a refusal.
 *
 * THE SECOND GATE IS A PERSON, and the TYPE decides whether it applies
 * (2026-09-15). The operator chooses Trial or Full; nothing is inferred from
 * the dates any more. A trial is offered only for a customer's FIRST licence
 * (crm-api's `trial_availability`, asked before a licence exists) — it lasts
 * 30 days and signs the moment Generate is pressed. A full licence is any term,
 * perpetual included, and ALWAYS waits: the super admins checked at System
 * Admin ▸ System Emails are emailed (nobody checked ⇒ no mail), nothing is
 * signed until a super admin presses Approve here, and until then the
 * "Licence file" card says it is waiting where the download will appear. The
 * button says which of the two it is about to do BEFORE it is pressed, and
 * Approve / Reject appear only for a holder of `crm:licences_approve`.
 *
 * RENEWAL IS A SUCCESSOR (2026-09-15). An issued licence is never edited; it
 * is RENEWED by drafting a new full licence for the same appliance — same
 * address, features and ports, a year longer by default — which then goes
 * through the ordinary Create Licence → approval path. crm-api opens the
 * window 30 days before expiry (`renewal.renewable` / `renewableFrom`), and
 * this page offers Renew licence only inside it, names the date outside it,
 * and links to the successor once one exists. On the successor's own page the
 * address is fixed (same appliance) and Trial is off the table (a renewal is
 * always a full licence).
 */

/** The ports an operator sets. `total` is not asked for (2026-09-15): the
 * appliance reads it only as the fallback when audio conferencing is unset, so
 * the payload carries the audio conferencing count in it (see `toPayload`). */
const VISIBLE_PORT_KEYS = LICENCE_PORT_KEYS.filter((key) => key !== "total")

const PORT_LABELS: Record<LicencePortKey, string> = {
  total: "Total",
  tdm: "TDM",
  audioConferencing: "Audio conferencing",
  webConferencing: "Web conferencing",
  networkSwitches: "Network switches",
  audioRecording: "Audio recording",
}

const PORT_HELP: Partial<Record<LicencePortKey, string>> = {
  audioConferencing: "Conferencing and mass notification are switched off if this is 0.",
}

const PORT_PAYLOAD_KEYS: Record<LicencePortKey, keyof LicencePayload> = {
  total: "ports_total",
  tdm: "ports_tdm",
  audioConferencing: "ports_audio_conferencing",
  webConferencing: "ports_web_conferencing",
  networkSwitches: "ports_network_switches",
  audioRecording: "ports_audio_recording",
}

const EMPTY_PORTS: LicencePorts = {
  total: 0,
  tdm: 0,
  audioConferencing: 0,
  webConferencing: 0,
  networkSwitches: 0,
  audioRecording: 0,
}

interface Draft {
  kind: LicenceKind
  label: string
  hardwareAddress: string
  expiresOn: string
  featureKeys: string[]
  ports: LicencePorts
}

function toDraft(licence: Licence): Draft {
  const kind: LicenceKind = licence.kind === "trial" ? "trial" : "full"
  return {
    // An older BFF sends no kind; a licence nobody chose as a trial is full.
    kind,
    // A licence somebody named keeps its name. One made before the label came
    // back — or a successor whose predecessor had none — gets the suggestion
    // rather than an empty box.
    label:
      licence.label ||
      suggestLicenceLabel({
        customerName: licence.customerName,
        kind,
        renewal: Boolean(licence.renewal?.renews),
      }),
    hardwareAddress: licence.hardwareAddress,
    expiresOn: licence.expiresOn ?? "",
    featureKeys: [...licence.featureKeys],
    ports: { ...licence.ports },
  }
}

/** A brand-new licence starts with nothing chosen: a 30-day trial when the
 * customer may still have one, otherwise a full licence. The LABEL is the one
 * field that arrives filled in — from the customer it is for (2026-09-16), so
 * a row says something without anyone typing. */
function emptyDraft(kind: LicenceKind, customerName: string): Draft {
  return {
    kind,
    label: suggestLicenceLabel({ customerName, kind }),
    hardwareAddress: "",
    expiresOn: defaultTrialEnd(),
    featureKeys: [],
    ports: { ...EMPTY_PORTS },
  }
}

/** Duplicate: the same terms for ANOTHER machine, so the address is the one
 * thing not copied — and neither is the label, which would otherwise give two
 * rows the same name. */
function duplicateDraft(source: Licence): Draft {
  // The customer already holds the source licence, so a trial is not theirs to have.
  return {
    ...toDraft(source),
    kind: "full",
    label: suggestLicenceLabel({ customerName: source.customerName, kind: "full" }),
    hardwareAddress: "",
  }
}

/** The terms that reach the signed file — what an approval is FOR. */
function signedTerms(draft: Draft): string {
  return [
    draft.kind,
    draft.hardwareAddress,
    draft.expiresOn,
    [...draft.featureKeys].sort().join(","),
    LICENCE_PORT_KEYS.map((key) => `${key}=${draft.ports[key]}`).join(","),
  ].join("|")
}

function toPayload(draft: Draft): LicencePayload {
  // The label is a human name and nothing else reads it — it is not part of
  // `signedTerms`, so editing it never voids an approval. Notes are still not
  // asked for (2026-09-15).
  const payload: LicencePayload = {
    kind: draft.kind,
    label: draft.label.trim().slice(0, LICENCE_LABEL_MAX),
    // The appliance's own form, whatever notation was typed (2026-09-15).
    hardware_address: normalizeHardwareAddress(draft.hardwareAddress) ?? draft.hardwareAddress.trim(),
    // An empty field is PERPETUAL, not "unset". The server omits `expires:`
    // entirely for null, which is what makes the licence perpetual on the box.
    expires_on: draft.expiresOn ? draft.expiresOn : null,
    feature_keys: draft.featureKeys,
  }
  for (const key of LICENCE_PORT_KEYS) {
    payload[PORT_PAYLOAD_KEYS[key]] = draft.ports[key] as never
  }
  // Not asked for any more: `total` is the appliance's fallback when audio
  // conferencing is unset, so it carries that count rather than a stale number.
  payload.ports_total = draft.ports.audioConferencing
  return payload
}

const NOT_REQUIRED: Licence["approval"] = {
  status: "not_required",
  requestedAt: null,
  requestedBy: null,
  approvedAt: null,
  approvedBy: null,
  note: null,
}

interface Target {
  customerId: number
  customerName: string
}

export default function LicenceBuilderPage() {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { canAccessLicences, canApproveLicences } = usePermissions()

  // `/crm/licences/new` carries no `:id` at all; `id === "new"` covers a
  // direct hit on the `:id` route by that word.
  const isNew = id === undefined || id === "new"
  const licenceId = isNew ? NaN : Number(id)
  const fromId = params.get("from") ? Number(params.get("from")) : null
  const customerParam = Number(params.get("customer"))
  const nameParam = params.get("name")

  const [licence, setLicence] = useState<Licence | null>(null)
  const [target, setTarget] = useState<Target | null>(null)
  const [catalog, setCatalog] = useState<LicenceCatalog>({ features: [], applianceKeys: [] })
  const [draft, setDraft] = useState<Draft | null>(null)
  // Whether the operator has written their own label. Until they do, the
  // suggestion follows the licence type; after, nothing overwrites it.
  const [labelTouched, setLabelTouched] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // May this customer still get a trial? Null while it is being asked.
  const [trialAvailable, setTrialAvailable] = useState<boolean | null>(null)
  // Generate on `/new` lands here with its result in router state. The same
  // element serves both routes, so React does not remount — read it on change.
  useEffect(() => {
    const handed = (location.state as { notice?: string } | null)?.notice
    if (handed) setNotice(handed)
  }, [location.state])
  const [warnings, setWarnings] = useState<string[]>([])
  const [confirmApprove, setConfirmApprove] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [rejectNote, setRejectNote] = useState("")
  const [confirmDelete, setConfirmDelete] = useState(false)

  const remove = async () => {
    setConfirmDelete(false)
    setBusy(true)
    setError(null)
    try {
      await deleteLicence(licenceId)
      navigate("/crm/licences", { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete this licence.")
      setBusy(false)
    }
  }
  const [downloads, setDownloads] = useState<LicenceDownloadRow[]>([])

  const loadDownloads = useCallback(async (which: number) => {
    try {
      setDownloads(await fetchLicenceDownloads(which))
    } catch {
      // The journal is a companion panel; a failure to load it must not take the
      // builder down with it. The page-level journal is the fallback.
      setDownloads([])
    }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setCatalog(await fetchLicenceCatalog())
      if (isNew) {
        if (fromId) {
          const source = await fetchLicence(fromId)
          setTarget({ customerId: source.customerId, customerName: source.customerName })
          setDraft(duplicateDraft(source))
          setTrialAvailable(false)
        } else {
          if (!customerParam) throw new Error("Pick a customer first.")
          setTarget({ customerId: customerParam, customerName: nameParam ?? `Customer #${customerParam}` })
          // Unknown is "no": offering a trial the server then refuses is worse
          // than asking for approval that turns out not to be needed.
          const available = await fetchTrialAvailability(customerParam).catch(() => false)
          setTrialAvailable(available)
          setDraft(emptyDraft(available ? "trial" : "full", nameParam ?? ""))
        }
        setLicence(null)
      } else {
        const row = await fetchLicence(licenceId)
        setLicence(row)
        setTarget({ customerId: row.customerId, customerName: row.customerName })
        setDraft(toDraft(row))
        // A label already on the RECORD is somebody's choice, not a suggestion:
        // switching the type must not rename a licence under them. One that was
        // only ever suggested stays open to the suggestion.
        setLabelTouched(row.label.trim() !== "")
        setTrialAvailable(row.trial?.available === true)
        void loadDownloads(licenceId)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this licence.")
    } finally {
      setLoading(false)
    }
  }, [isNew, fromId, licenceId, customerParam, nameParam, loadDownloads])

  useEffect(() => {
    void load()
  }, [load])

  const gateErrors = useMemo(
    () => (draft ? portGateErrors({ featureKeys: draft.featureKeys, ports: draft.ports }) : []),
    [draft],
  )
  const gateWarnings = useMemo(
    () => (draft ? portGateWarnings({ featureKeys: draft.featureKeys, ports: draft.ports }) : []),
    [draft],
  )
  const addressValid = draft ? isValidHardwareAddress(draft.hardwareAddress) : false

  const setPort = (key: LicencePortKey, value: number) => {
    setDraft((current) => (current ? { ...current, ports: { ...current.ports, [key]: value } } : current))
  }

  const toggleFeature = (key: string) => {
    setDraft((current) => {
      if (!current) return current
      const has = current.featureKeys.includes(key)
      return {
        ...current,
        featureKeys: has
          ? current.featureKeys.filter((existing) => existing !== key)
          : [...current.featureKeys, key],
      }
    })
  }

  const describeOutcome = (result: IssueResult): string => {
    if (result.outcome === "approval_requested") {
      return "Sent for approval. The super admins checked at System Emails have been emailed; nothing is signed until a super admin approves it, and the download appears here then."
    }
    if (result.outcome === "already_pending") {
      return "Already waiting for approval — nothing was re-sent."
    }
    const serial = result.licence.currentIssue?.serial ?? ""
    return result.licence.kind === "trial"
      ? `Generated ${serial} as a 30-day trial. Its expiry is on the Tasks calendar.`
      : `Generated ${serial}. Its expiry is on the Tasks calendar.`
  }

  /** Generate: create (or update) the row, then sign — one press. */
  const generate = async () => {
    if (!draft || !target) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      if (isNew) {
        const created = await createLicence({ ...toPayload(draft), customer_id: target.customerId })
        let result: IssueResult
        try {
          result = await issueLicence(created.id)
        } catch (err) {
          // No half-made licence: the row was only ever a means to a file.
          await deleteLicence(created.id).catch(() => undefined)
          throw err
        }
        navigate(`/crm/licences/${created.id}`, { replace: true, state: { notice: describeOutcome(result) } })
        return
      }
      // The label is NOT a signed term — deliberately, so renaming never voids
      // an approval — which means the terms check alone would drop a rename on
      // the floor. Save when either has changed.
      if (licence && (signedTerms(draft) !== signedTerms(toDraft(licence)) || draft.label.trim() !== licence.label)) {
        const saved = await updateLicence(licenceId, toPayload(draft))
        setLicence(saved)
        setDraft(toDraft(saved))
      }
      const result = await issueLicence(licenceId)
      setLicence(result.licence)
      setDraft(toDraft(result.licence))
      setWarnings(result.warnings.map((warning) => warning.message))
      setNotice(describeOutcome(result))
      void loadDownloads(licenceId)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not generate this licence.")
    } finally {
      setBusy(false)
    }
  }

  const approve = async () => {
    setConfirmApprove(false)
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const approved = await approveLicence(licenceId)
      setLicence(approved)
      setDraft(toDraft(approved))
      setNotice(`Approved and generated ${approved.currentIssue?.serial ?? ""}. The requester has been emailed.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not approve this licence.")
    } finally {
      setBusy(false)
    }
  }

  const reject = async () => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const rejected = await rejectLicence(licenceId, rejectNote.trim())
      setLicence(rejected)
      setRejecting(false)
      setRejectNote("")
      setNotice("Rejected. The requester has been emailed your reason.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not reject this licence.")
    } finally {
      setBusy(false)
    }
  }

  /** Renew: draft the successor upstream and open it — the operator picks the
   * term there and presses Create Licence, which asks for approval. */
  const renew = async () => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const successor = await renewLicence(licenceId)
      const serial = licence?.currentIssue?.serial ?? licence?.label ?? "this licence"
      navigate(`/crm/licences/${successor.id}`, {
        state: { notice: `Renewal drafted from ${serial} — choose the term and press Create Licence; it needs approval.` },
      })
    } catch (err) {
      // A 422 from crm-api carries the sentence that matters ("can be renewed from …").
      setError(err instanceof Error ? err.message : "Could not renew this licence.")
      setBusy(false)
    }
  }

  const download = async () => {
    setBusy(true)
    setError(null)
    try {
      const blob = await downloadLicenceFile(licenceId)
      // saveBlob, never a hand-rolled <a download>: the desktop shell drops that
      // into an opaque default with no save prompt, and a licence file the
      // customer cannot find is a support call.
      const serial = licence?.currentIssue?.serial ?? "licence"
      await saveBlob(`${licence?.customerName ?? "licence"}-${serial}.zip`, blob)
      setLicence(await fetchLicence(licenceId))
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not download the licence file.")
    } finally {
      setBusy(false)
      // Allowed or refused, the pull is in the journal now.
      void loadDownloads(licenceId)
    }
  }

  if (!canAccessLicences()) {
    return (
      <Typography sx={{ py: 6, textAlign: "center" }} color="text.secondary">
        You do not have access to licences.
      </Typography>
    )
  }

  if (loading) return <PageLoader />
  if (!draft || !target) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography color="error">{error ?? "Licence not found."}</Typography>
      </Box>
    )
  }

  const issued = licence?.currentIssue ?? null
  const approval = licence?.approval ?? NOT_REQUIRED
  const pending = approval.status === "pending"
  // Issued or awaiting a decision: nothing here may change. Duplicate instead.
  const locked = Boolean(issued) || pending
  const trialTerm = draft.kind === "trial"
  const dirty = licence ? signedTerms(draft) !== signedTerms(toDraft(licence)) : true
  const approvedForTheseTerms = approval.status === "approved" && !dirty
  // What the one button is about to DO, said before it is pressed.
  // What stops Create Licence, in words — shown beside the button, never a
  // silently greyed-out control (2026-09-15).
  const addressProblem = hardwareAddressProblem(draft.hardwareAddress)
  const blockers = locked
    ? []
    : [
        ...(draft.hardwareAddress.trim() ? [] : ["Enter the hardware address."]),
        ...(addressProblem ? [addressProblem] : []),
        ...gateErrors,
      ]
  const canGenerate = !locked && blockers.length === 0 && addressValid && !busy
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (canGenerate) void generate()
  }
  const createButton = (placement: "top" | "bottom") => (
    <Button
      type="submit"
      variant="contained"
      startIcon={<KeyRound size={15} />}
      disabled={!canGenerate}
      data-testid={`licence-generate-${placement}`}
      title={blockers.length > 0 ? blockers.join(" ") : undefined}
    >
      {busy ? "Creating…" : "Create Licence"}
    </Button>
  )
  const canDecide = pending && canApproveLicences()
  const title = `${target.customerName} — ${isNew ? "New licence" : licence?.label || draft.hardwareAddress || "Licence"}`
  // One of OUR OWN boxes. Read from the saved row when there is one; before
  // Generate there is no row, so the dialog passes it in the URL. Presentation
  // only — crm-api gates the write on the customer, not on this.
  const internalTarget = licence?.customerInternal ?? params.get("internal") === "1"
  // Renewal (2026-09-15). `renews` set = THIS page is a successor: same
  // appliance, so the address is fixed and Trial is not offered.
  const renewal = licence?.renewal ?? null
  const renewsFrom = renewal?.renews ?? null
  const isRenewal = renewsFrom !== null
  const successor = renewal?.successor ?? null
  const renewalOpensLater = Boolean(
    renewal && !renewal.renewable && !successor && renewal.renewableFrom && renewal.renewableFrom > todayIso(),
  )

  return (
    <PagePermissionGuard requiredPermission="licences:access">
      {/* One form: Enter in any field creates the licence, and the button at
          the top and the one at the bottom are the same submit. */}
      <Box component="form" noValidate onSubmit={submit}>
        <BackButton to="/crm/licences" label="Licences" />
        <PageHeader
          title={title}
          description={
            internalTarget
              ? locked
                ? "One of XOP's own appliances. A generated licence is locked — duplicate it to license another machine or different terms."
                : "One of XOP's own appliances. Pick the terms and generate the file in one step. A trial issues at once; a full licence waits for a super admin's approval, exactly as it would for a customer."
              : locked
                ? "What this appliance may run. A generated licence is locked — duplicate it to license another machine or different terms."
                : "What this appliance may run. Pick the terms and generate the file in one step. Distinct from the agreement, which is what the customer bought."
          }
          actions={
            licence || !locked ? (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {licence && issued && renewal?.renewable ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => void renew()}
                    disabled={busy}
                    data-testid="licence-renew"
                  >
                    Renew licence
                  </Button>
                ) : null}
                {licence && issued && successor ? (
                  <Link
                    component={RouterLink}
                    to={`/crm/licences/${successor.id}`}
                    data-testid="licence-renewal-successor"
                    sx={{ fontSize: 13 }}
                  >
                    Renewal {successor.serial ?? `#${successor.id}`} · {successorState(successor)}
                  </Link>
                ) : null}
                {licence && issued && renewalOpensLater && renewal ? (
                  <Typography variant="caption" color="text.secondary" data-testid="licence-renewal-opens">
                    Renewal opens {formatTermDate(renewal.renewableFrom ?? "")} ({renewal.windowDays} days before expiry)
                  </Typography>
                ) : null}
                {licence && issued ? (
                  <Button variant="outlined" onClick={() => navigate(`/crm/licences/new?from=${licence.id}`)}>
                    Duplicate
                  </Button>
                ) : null}
                {licence ? (
                  <Button variant="outlined" color="error" onClick={() => setConfirmDelete(true)} disabled={busy}>
                    Delete
                  </Button>
                ) : null}
                {locked ? null : createButton("top")}
              </Stack>
            ) : undefined
          }
        />

        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
        {notice ? <Alert severity="success" sx={{ mt: 2 }}>{notice}</Alert> : null}
        {renewsFrom ? (
          <Alert severity="info" sx={{ mt: 2 }} data-testid="licence-renews">
            Renews{" "}
            <Link component={RouterLink} to={`/crm/licences/${renewsFrom.id}`} color="inherit">
              {renewsFrom.serial ?? `#${renewsFrom.id}`}
            </Link>
            {renewsFrom.expiresOn ? `, which expires ${formatTermDate(renewsFrom.expiresOn)}` : ""}. Same appliance — the
            address cannot change.
          </Alert>
        ) : null}

        {pending ? (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            action={
              canDecide ? (
                <Stack direction="row" spacing={1}>
                  <Button color="inherit" size="small" onClick={() => setRejecting(true)} disabled={busy}>
                    Reject
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    onClick={() => setConfirmApprove(true)}
                    disabled={busy || gateErrors.length > 0}
                  >
                    Approve
                  </Button>
                </Stack>
              ) : undefined
            }
          >
            <strong>Waiting for approval.</strong> Requested by {approval.requestedBy ?? "—"} on{" "}
            {approval.requestedAt?.slice(0, 10) ?? "—"}. The super admins checked at System Emails have been emailed;
            nothing is signed until a super admin approves it, and the terms are locked until then.
          </Alert>
        ) : null}
        {approval.status === "rejected" ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            <strong>Not approved</strong> by {approval.approvedBy ?? "a super admin"} on{" "}
            {approval.approvedAt?.slice(0, 10) ?? "—"}
            {approval.note ? ` — “${approval.note}”` : "."} Change the terms and send it for approval again.
          </Alert>
        ) : null}
        {approvedForTheseTerms && !issued ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Approved by {approval.approvedBy ?? "a super admin"} on {approval.approvedAt?.slice(0, 10) ?? "—"} for exactly
            these terms. Changing any of them voids the approval.
          </Alert>
        ) : null}

        {gateErrors.map((message) => (
          <Alert key={message} severity="error" sx={{ mt: 2 }}>
            {message}
          </Alert>
        ))}
        {[...gateWarnings, ...warnings].map((message) => (
          <Alert key={message} severity="warning" sx={{ mt: 2 }}>
            {message}
          </Alert>
        ))}

        <Grid container spacing={3} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Appliance
                </Typography>
                <Stack spacing={2}>
                  <LicenceTypeChoice
                    kind={draft.kind}
                    trialAvailable={isRenewal ? false : trialAvailable}
                    trialUnavailableReason={isRenewal ? "A renewal is always a full licence." : undefined}
                    disabled={locked}
                    onChange={(kind) =>
                      setDraft({
                        ...draft,
                        kind,
                        expiresOn: kind === "trial" ? defaultTrialEnd() : draft.expiresOn,
                        // The suggestion follows the type until somebody types
                        // their own label; after that it is theirs.
                        label: labelTouched
                          ? draft.label
                          : suggestLicenceLabel({ customerName: target.customerName, kind }),
                      })
                    }
                  />
                  <TextField
                    label="Label"
                    size="small"
                    value={draft.label}
                    disabled={locked}
                    inputProps={{ maxLength: LICENCE_LABEL_MAX }}
                    onChange={(event) => {
                      setLabelTouched(true)
                      setDraft({ ...draft, label: event.target.value })
                    }}
                    helperText="A short name for this licence — it is what the list, the approval request and the mail to XOP's executives call it. Prefilled from the customer; write whatever you recognise."
                  />
                  <TextField
                    label="Hardware address"
                    size="small"
                    value={draft.hardwareAddress}
                    disabled={locked || isRenewal}
                    onChange={(event) => setDraft({ ...draft, hardwareAddress: event.target.value })}
                    onBlur={() => {
                      const canonical = normalizeHardwareAddress(draft.hardwareAddress)
                      if (canonical && canonical !== draft.hardwareAddress) setDraft({ ...draft, hardwareAddress: canonical })
                    }}
                    error={Boolean(addressProblem)}
                    helperText={
                      addressProblem ??
                      (isRenewal
                        ? "The renewed appliance. A renewal is for the same machine; licence another with Duplicate."
                        : "Its system id or MAC address — any standard notation (AA:BB:CC:DD:EE:FF, AA-BB-CC-DD-EE-FF, aabb.ccdd.eeff). Saved as the appliance reports it.")
                    }
                  />
                  {trialTerm ? (
                    // A trial's term is not a choice: 30 days, fixed.
                    <Box
                      data-testid="licence-trial-term"
                      sx={{ px: 1.5, py: 1, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "surfaceSoft" }}
                    >
                      <Typography variant="caption" color="text.secondary" component="div">
                        Expires
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Ends {formatTermDate(draft.expiresOn)} · 30 days
                      </Typography>
                    </Box>
                  ) : (
                    <TextField
                      label="Expires"
                      type="date"
                      size="small"
                      value={draft.expiresOn}
                      disabled={locked}
                      onChange={(event) => setDraft({ ...draft, expiresOn: event.target.value })}
                      InputLabelProps={{ shrink: true }}
                      helperText={
                        draft.expiresOn
                          ? "The term ends at the end of this day. Its expiry goes on the Tasks calendar when generated."
                          : "Empty means PERPETUAL — the licence file carries no expiry at all."
                      }
                    />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Ports
                </Typography>
                <Grid container spacing={2}>
                  {VISIBLE_PORT_KEYS.map((key) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <PortCountField
                        fullWidth
                        size="small"
                        label={PORT_LABELS[key]}
                        value={draft.ports[key]}
                        disabled={locked}
                        onValueChange={(value) => setPort(key, value)}
                        helperText={PORT_HELP[key] ?? " "}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {issued ? (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Licence file
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {issued.serial} · generated {issued.issuedAt.slice(0, 10)} by {issued.issuedByLabel}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: 11 }}>
                    sha256 {issued.sha256.slice(0, 16)}…
                  </Typography>
                  {issued.downloadLimit !== null ? (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {issued.downloadsRemaining} of {issued.downloadLimit} downloads left (capped by an administrator)
                    </Typography>
                  ) : null}
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button variant="contained" onClick={() => void download()} disabled={busy}>
                      Download
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ) : pending || approval.status === "rejected" ? (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Licence file
                  </Typography>
                  {pending ? (
                    <Alert severity="info" data-testid="licence-awaiting-approval">
                      <strong>Waiting for approval.</strong> The download appears here once a super admin approves
                      this licence.
                    </Alert>
                  ) : (
                    <Alert severity="error" data-testid="licence-not-approved">
                      <strong>Not approved.</strong> {approval.note ?? "No file was generated."}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            ) : null}
          </Grid>

          {licence ? (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Downloads
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Every pull of this licence&apos;s file — who, when, from where. Refusals are recorded too.
                  </Typography>
                  <DownloadJournalTable rows={downloads} />
                </CardContent>
              </Card>
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Features
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  What this appliance may run, in the two sections its own licence page shows: pick the Value Added
                  Services and Platform Extensions the customer bought. Prices come from CRM ▸ Pricing.
                </Typography>
                <LicenceFeaturePicker
                  catalog={catalog}
                  selectedKeys={draft.featureKeys}
                  disabled={locked}
                  onToggle={toggleFeature}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {locked ? (
          <Typography variant="body2" color="text.secondary">
            {issued
              ? "This licence has been generated and cannot be edited. Use Duplicate for another machine or different terms."
              : "Waiting for approval — the terms are locked until a super admin decides."}
          </Typography>
        ) : (
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap>
            {blockers.length > 0 ? (
              <Box data-testid="licence-generate-blockers" sx={{ color: "text.secondary", fontSize: 13, textAlign: "right" }}>
                {blockers.map((blocker) => (
                  <div key={blocker}>{blocker}</div>
                ))}
              </Box>
            ) : null}
            {createButton("bottom")}
          </Stack>
        )}

        <ConfirmDialog
          isOpen={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          title="Delete this licence?"
          description={
            issued
              ? "The generated file, its download history and its expiry task go with it. A file the customer already installed keeps working on the appliance."
              : "Nothing has been generated for it."
          }
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => remove()}
        />
        <ConfirmDialog
          isOpen={confirmApprove}
          onClose={() => setConfirmApprove(false)}
          title="Approve and generate this licence?"
          description={`${target.customerName} — ${draft.hardwareAddress}: ${draft.expiresOn ? `until ${draft.expiresOn}` : "PERPETUAL"}, ${draft.ports.audioConferencing} audio / ${draft.ports.webConferencing} web ports, ${draft.featureKeys.length} features. Approving signs the file now and emails the requester that it is ready to download.`}
          confirmLabel="Approve and generate"
          onConfirm={() => approve()}
        />
        <Dialog open={rejecting} onClose={busy ? undefined : () => setRejecting(false)} fullWidth maxWidth="sm">
          <DialogTitle>Reject this licence request?</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              The requester reads your reason in the email. Nothing is signed; they can change the terms and ask
              again.
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={2}
              label="Reason"
              value={rejectNote}
              onChange={(event) => setRejectNote(event.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejecting(false)} disabled={busy}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={() => void reject()} disabled={busy || !rejectNote.trim()}>
              Reject
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PagePermissionGuard>
  )
}

/** Today as an ISO date in LOCAL time, comparable to crm-api's dates. */
function todayIso(): string {
  const date = new Date()
  const month = `${date.getMonth() + 1}`.padStart(2, "0")
  const day = `${date.getDate()}`.padStart(2, "0")
  return `${date.getFullYear()}-${month}-${day}`
}

/** Where a drafted renewal has got to, in two words. */
export function successorState(successor: NonNullable<Licence["renewal"]["successor"]>): string {
  if (successor.status === "issued") return "generated"
  if (successor.approvalStatus === "pending") return "waiting for approval"
  if (successor.approvalStatus === "rejected") return "not approved"
  return "drafted"
}

/** "Oct 15, 2026" from an ISO date; the raw value if it cannot be read. */
function formatTermDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`)
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

/**
 * TRIAL OR FULL — the licence's type, chosen here and never inferred from its
 * dates (2026-09-15). A trial is only for a customer's FIRST licence: it signs
 * the moment Generate is pressed and the file is downloadable at once. A full
 * licence is any term, perpetual included, and always waits for a super
 * admin's approval. crm-api enforces both; this only offers what it will accept.
 */
function LicenceTypeChoice({
  kind,
  trialAvailable,
  trialUnavailableReason,
  disabled,
  onChange,
}: {
  kind: LicenceKind
  trialAvailable: boolean | null
  /** Why Trial is off the table, when it is for a reason other than "already
   * has a licence" — a renewal, say. */
  trialUnavailableReason?: string | undefined
  disabled: boolean
  onChange: (kind: LicenceKind) => void
}) {
  const options = [
    {
      value: "trial" as const,
      title: "Trial licence",
      caption: "Available immediately",
      body:
        trialAvailable === false
          ? (trialUnavailableReason ?? "Not available — this customer already has a licence.")
          : "30 days. The file is generated and ready to download as soon as you press Generate.",
      Icon: Zap,
      unavailable: trialAvailable !== true,
    },
    {
      value: "full" as const,
      title: "Full licence",
      caption: "Needs approval",
      body: "Any term, or perpetual. The super admins checked at System Emails are emailed, and the download appears here once a super admin approves it.",
      Icon: ShieldCheck,
      unavailable: false,
    },
  ]

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.75 }}>
        Licence type
      </Typography>
      <Box
        role="radiogroup"
        aria-label="Licence type"
        sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}
      >
        {options.map(({ value, title, caption, body, Icon, unavailable }) => {
          const on = kind === value
          return (
            <ButtonBase
              key={value}
              role="radio"
              aria-checked={on}
              aria-label={title}
              disabled={disabled || (unavailable && !on)}
              onClick={() => onChange(value)}
              sx={(theme) => ({
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                textAlign: "left",
                gap: 1.25,
                p: 1.5,
                borderRadius: 1.5,
                border: 1,
                borderColor: on ? theme.palette.primary.main : theme.palette.divider,
                bgcolor: on ? alpha(theme.palette.primary.main, 0.06) : "background.paper",
                "&:hover": { borderColor: on ? theme.palette.primary.main : theme.palette.text.disabled },
                "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                "&.Mui-disabled": { opacity: on ? 0.85 : 0.55 },
              })}
            >
              <Box sx={{ color: on ? "primary.main" : "text.secondary", display: "flex", mt: "2px" }}>
                <Icon size={18} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {title}
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ fontWeight: 600, color: value === "trial" ? "success.main" : "warning.main" }}
                >
                  {caption}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                  {body}
                </Typography>
              </Box>
            </ButtonBase>
          )
        })}
      </Box>
    </Box>
  )
}
